/**
 * THE definition of the "this row's date is UNKNOWN" placeholder window, shared
 * by the producer (backend staging) and every consumer that must NOT show it.
 *
 * A staged import row whose date cell the engine refused to interpret still
 * needs a value for `imported_transactions.date` (`timestamp NOT NULL`), so the
 * backend stamps a deliberately impossible timestamp: the first day of year
 * 9999, offset by the row's source line. See
 * `packages/backend/src/modules/accounting/features/imports/domain/services/unparseable-import-date.ts`
 * for the full rationale (unmistakable, deterministic, distinct per row, above
 * every `importFromDate` floor).
 *
 * WHY THE PREDICATE LIVES HERE AND NOT IN EACH PACKAGE: the needs-attention
 * tray rendered `01/01/9999 00:00:01` as if it were the transaction's real date,
 * because the frontend had no way to tell a placeholder from a timestamp. A
 * second, hand-copied copy of the window bounds is exactly the drift this
 * codebase keeps re-discovering (two sources for one label), so both sides read
 * THIS module.
 */

/** Start of the placeholder window: `9999-01-01T00:00:00.000Z`. */
export const UNPARSEABLE_IMPORT_DATE_WINDOW_START_MS = Date.UTC(9999, 0, 1)

/**
 * Length of the window — the 24 hours after the start. One CSV cannot plausibly
 * carry 86 400 rows of unparseable dates, and no genuine transaction lives in
 * that window.
 */
export const UNPARSEABLE_IMPORT_DATE_WINDOW_LENGTH_MS = 86_400_000

/**
 * True when `epochMs` falls inside the placeholder window — i.e. the row's real
 * date is UNKNOWN and this value is a NOT NULL filler, never a real timestamp.
 * A non-finite input is NOT a placeholder (it is a different kind of broken).
 */
export function isUnparseableImportDatePlaceholderEpochMs(epochMs: number): boolean {
  return (
    Number.isFinite(epochMs) &&
    epochMs >= UNPARSEABLE_IMPORT_DATE_WINDOW_START_MS &&
    epochMs < UNPARSEABLE_IMPORT_DATE_WINDOW_START_MS + UNPARSEABLE_IMPORT_DATE_WINDOW_LENGTH_MS
  )
}

/**
 * True when an ISO-8601 string (as the needs-attention API serialises a staged
 * row's `timestamp`) is a placeholder. An empty or unparseable string is NOT a
 * placeholder — callers already have an em-dash path for that.
 */
export function isUnparseableImportDatePlaceholderIso(isoTimestamp: string): boolean {
  if (isoTimestamp.trim() === '') return false
  return isUnparseableImportDatePlaceholderEpochMs(new Date(isoTimestamp).getTime())
}
